/**
 * Servicio de WebSocket para comunicación con LLM
 * Basado en LoginWebSocket.cs y WebSocketService.cs
 */

import { ChatMessage, WebSocketConfig, PracticeMode, AccentType } from '../types';
import configService from './configService';
import conversationSaverService from './conversationSaverService';
import interactionTimeService from './interactionTimeService';
import academicService, { LessonObjective } from './academicService';
import academicEvaluationService from './academicEvaluationService';
import objectiveEvaluationService, { ObjectiveEvaluationResult } from './objectiveEvaluationService';
import assistantResponseEvaluationService, { AssistantResponseQuality } from './assistantResponseEvaluationService';
import promptModifierService from './promptModifierService';
// V3 (consolidación fase 3, 2026-07-14): Profe/Conversadora disjuntos, una
// fuente por regla, ~50% menos tokens, orden cache-friendly. V2 queda en el
// repo como referencia/rollback.
// Prompt V4 (consolidación 17/09/2026): misma firma que V3, -24 % de tamaño.
// V3 queda en el repo como rollback (cambiar este import y reconstruir).
import { assembleStudentPromptV4 as assembleFullPrompt } from '../config/promptsV4';
import { PERSONALITY_TONE_REMINDERS } from '../config/prompts';
import { assembleAideProfPrompt } from '../config/aideProfPrompt';
import authService from './authService';
import sessionService from './sessionService';
import { logger } from '../utils/logger';
import { getVoiceConfig } from '../config/voiceConfig';

export interface WebSocketMessage {
  role: 'user' | 'assistant';
  content: string;
}

/** Quita los prefijos de canal `[text]` / `[audio]` que el contenido ya traiga. */
const stripChannelPrefix = (text: string): string =>
  text.replace(/^(?:\s*\[(?:audio|text|texte)\]\s*)+/i, '');

export class WebSocketService {
  /**
   * Verifica si un token JWT está expirado o cerca de expirar
   * @param token Token JWT a verificar
   * @param bufferSeconds Segundos de margen antes de considerar el token como expirado (default: 60)
   * @returns true si el token está expirado o cerca de expirar
   */
  private isTokenExpired(token: string, bufferSeconds: number = 60): boolean {
    try {
      if (!token || token.trim() === '') {
        return true;
      }

      // Dividir el token en sus tres partes (Header, Payload, Signature)
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        console.warn('[WebSocketService] Token no tiene formato JWT válido');
        return true;
      }

      // Decodificar el Payload (parte 2 del token)
      const payload = tokenParts[1];
      let decodedPayload: string;
      try {
        // Agregar padding si es necesario para Base64
        let padded = payload;
        while (padded.length % 4) {
          padded += '=';
        }
        decodedPayload = atob(padded);
      } catch (error) {
        console.warn('[WebSocketService] Error al decodificar payload del token:', error);
        return true;
      }

      // Parsear el JSON del Payload
      let payloadData: any;
      try {
        payloadData = JSON.parse(decodedPayload);
      } catch (error) {
        console.warn('[WebSocketService] Error al parsear payload del token:', error);
        return true;
      }

      // Verificar si el atributo 'exp' está presente
      if (payloadData.exp) {
        const expirationTime = payloadData.exp * 1000; // Convertir a milisegundos
        const currentTime = Date.now();
        const bufferTime = bufferSeconds * 1000; // Convertir buffer a milisegundos
        
        // Considerar expirado si falta menos de bufferSeconds para expirar
        const isExpired = (expirationTime - currentTime) < bufferTime;
        
        if (isExpired) {
          const secondsUntilExpiry = Math.floor((expirationTime - currentTime) / 1000);
          logger.log(`[WebSocketService] Token expira en ${secondsUntilExpiry} segundos (buffer: ${bufferSeconds}s)`);
        }
        
        return isExpired;
      } else {
        // Si no hay campo 'exp', considerar el token como válido pero loguear advertencia
        console.warn('[WebSocketService] Token no tiene campo "exp", no se puede verificar expiración');
        return false;
      }
    } catch (error) {
      console.error('[WebSocketService] Error al verificar expiración del token:', error);
      return true; // En caso de error, considerar como expirado para forzar renovación
    }
  }

  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnecting = false;
  private isInitializing = false;
  private chathistory: ChatMessage[] = []; // Historial de chat
  
  // Colas para chunks (modelo Unity: 1:1 texto/audio)
  private messageQueue: Array<{ chunk: string; receivedAt: number }> = []; // Cola de chunks de texto + timestamp de recepción
  private audioQueue: Array<{ url: string; blob: Blob }> = []; // Cola de chunks de audio
  private accumulatedChunks = ''; // Acumulador para historial final
  // Almacenar blobs de audio para reproducir después (no revocar URLs inmediatamente)
  private audioBlobStore: Map<string, Blob> = new Map(); // Map<audioUrl, Blob>
  private pendingSendQueue: Array<{ payload: string; type: 'audio' | 'text' }> = []; // Cola de mensajes pendientes de enviar
  private onMessageCallback?: (message: ChatMessage) => void;
  // @ts-ignore - Se usa en onAudio() y offAudio()
  private onAudioCallback?: (audioUrl: string) => void;
  private onAvatarStateCallback?: (isHablando: boolean) => void;
  private onConnectionReadyCallback?: () => void; // Callback para notificar cuando la conexión esté lista
  private onChatHistoryClearedCallback?: () => void; // Callback para notificar cuando se limpia el historial
  // Estado del STT (audio→texto) para feedback al usuario en la UI:
  // 'transcribing' al enviar audio, 'done' si se reconoció, 'not_recognized' si
  // el STT no entendió (vacío/alucinación). App.tsx muestra "Transcribiendo…" y
  // un aviso de "no te entendí".
  private onTranscriptionStateCallback?: (state: 'transcribing' | 'done' | 'not_recognized') => void;
  
  // Estado interno para manejar LLM y transcripción
  private mainLLM: string = '';
  private mainModel: string = '';
  private backupLLM: string = '';
  private backupModel: string = '';
  private transcriptionModel: string = '';
  private canSendMessage: boolean = true;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private expectingTranscription: boolean = false; // Flag para detectar transcripción
  // Opción B (hilo único): recordatorio de tono pendiente. Cuando el usuario
  // cambia personalidad/funcionalidad/nivel SIN reiniciar el hilo, el historial
  // previo (~60K chars del estilo anterior) "ancla" al LLM por in-context
  // learning y diluye la instrucción nueva. Inyectamos un turno user enfático al
  // final del contexto (máxima recencia) para que el cambio de tono se note ya.
  // Se consume (se limpia) en el primer turno tras el cambio.
  private pendingBehaviorReminder: string = '';
  // Texto del documento adjuntado por el profesor (modo aide-prof). Se inyecta
  // en el contexto de sistema en cada mensaje; no se muestra en el chat ni se
  // guarda en el historial. Se limpia al iniciar una sesión nueva.
  private teacherDocumentText: string = '';
  private teacherDocumentName: string = '';
  private isResponseInterrupted: boolean = false; // Flag para ignorar chunks de respuestas interrumpidas
  private interruptedResponseChunks: string[] = []; // Acumular chunks de respuesta interrumpida para detectar fin
  private interruptedAudioChunks: Blob[] = []; // Acumular audio de respuesta interrumpida
  private lastUserMessageWasEmpty: boolean = false; // Flag para indicar que el último mensaje del usuario fue vacío
  
  // Identificador único de sesión de chat
  private sessionId: string;
  
  // Control de reproducción de audio
  private isPlayingAudio: boolean = false;
  private currentAudioElement: HTMLAudioElement | null = null;
  // Grace period antes de declarar fin de respuesta. Los chunks llegan en streaming
  // con latencia variable; si reproducimos uno y las colas quedan vacias, esperamos
  // un poco antes de finishResponse — si llega otro chunk lo cancelamos. Sin esto
  // el avatar parpadea y a veces se percibe como "corte" de audio cuando el
  // siguiente chunk llega tras un microhueco de red.
  private pendingFinishTimer: ReturnType<typeof setTimeout> | null = null;
  // Subimos el grace de 600 -> 1500 ms como red de seguridad por si el marker
  // __END_OF_RESPONSE__ no llega (backend viejo, drop de paquete, etc).
  private static readonly FINISH_GRACE_MS = 1500;
  // True cuando el backend emitio __END_OF_RESPONSE__: el siguiente audio.onended
  // con colas vacias puede llamar finishResponse() sin esperar el grace.
  private responseEndMarkerReceived: boolean = false;
  private wasPlayingBeforeHidden: boolean = false; // Flag para saber si estaba reproduciendo antes de ocultar
  private visibilityChangeHandler: (() => void) | null = null;
  private isManualAudioPlaying: boolean = false; // Flag para saber si hay audio manual reproduciéndose
  private isClearingQueues: boolean = false; // Flag para ignorar mensajes entrantes durante limpieza

  // Silenciar a Galia (TTS). Cuando es false, el cliente NO reproduce el audio
  // automático de las respuestas (solo se lee el texto) y avisa al backend con
  // audio_enabled:false para que pueda omitir la generación de TTS y ahorrar uso.
  // El estudiante alterna esto con el botón de altavoz; el modo profesor arranca
  // silenciado por defecto. La preferencia del estudiante se persiste en localStorage.
  private audioEnabled: boolean = true;

  // Estado para modo académico
  private academicMode: {
    isActive: boolean;
    level?: string;
    unit?: number;
    lesson?: number;
    objectives?: LessonObjective[];
    personalizedTask?: string; // Tarea personalizada para esta lección
    userEmail?: string;
    onObjectiveUpdate?: (objectiveDescription: string, isCorrect: boolean) => void;
    onEvaluationComplete?: () => void; // Callback cuando se completa la evaluación de la rúbrica
  } = {
    isActive: false,
  };

  // Guardar el prompt para poder enviarlo como mensaje inicial
  private currentPrompt: string = '';
  /**
   * Firma de la configuración con la que se armó `currentPrompt`. Si cambia con
   * la conexión viva, initialize() reconecta en vez de devolver temprano (ver
   * comentario allí).
   */
  private currentPromptKey: string | null = null;

  /** Campos de configuración que determinan el prompt ensamblado. */
  private promptKeyFor(config: ReturnType<typeof configService.getConfig>): string {
    return [
      config.practiceMode, config.personalityType, config.functionalityType,
      config.mcerLevel, config.accent, config.email, config.promptReference,
    ].map((v) => v ?? '').join('|');
  }

  constructor() {
    // Generar un ID único de sesión basado en timestamp y número aleatorio
    this.sessionId = `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Restaurar la preferencia de audio del estudiante (default: activado).
    try {
      this.audioEnabled = localStorage.getItem('galia_audio_enabled') !== 'false';
    } catch {
      this.audioEnabled = true;
    }

    // Configurar listener para detectar cuando la página pierde/recupera el foco
    this.setupVisibilityHandlers();
  }

  /**
   * Configurar handlers para detectar cuando la página pierde/recupera el foco
   */
  private setupVisibilityHandlers(): void {
    if (typeof document === 'undefined') return; // SSR safety
    
    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        // Página oculta - pausar audio
        this.pauseAudioOnHidden();
      } else {
        // Página visible - reanudar audio si estaba reproduciendo
        this.resumeAudioOnVisible();
        
        // Verificar si el WebSocket está desconectado y reconectar si es necesario
        this.checkAndReconnectOnVisible();
      }
    };
    
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  /**
   * Verificar y reconectar WebSocket cuando la página vuelve a ser visible
   */
  private checkAndReconnectOnVisible(): void {
    // Verificar si el WebSocket está cerrado o no existe
    const isDisconnected = !this.ws || 
                          this.ws.readyState === WebSocket.CLOSED || 
                          this.ws.readyState === WebSocket.CLOSING;
    
    // Solo reconectar si:
    // 1. El WebSocket está desconectado
    // 2. No estamos ya intentando conectar
    // 3. No hemos excedido los intentos de reconexión
    if (isDisconnected && !this.isConnecting && !this.isInitializing && 
        this.reconnectAttempts < this.maxReconnectAttempts) {
      // Resetear contador de intentos para permitir reconexión
      this.reconnectAttempts = 0;
      
      // Intentar reconectar
      this.initialize().catch((error) => {
        // console.error(`[${this.sessionId}] [Reconexión] Error al reconectar:`, error);
      });
    }
  }

  /**
   * Pausar audio cuando la página se oculta
   */
  private pauseAudioOnHidden(): void {
    if (this.currentAudioElement && !this.currentAudioElement.paused) {
      this.wasPlayingBeforeHidden = true;
      this.currentAudioElement.pause();
    }
  }

  /**
   * Reanudar audio cuando la página vuelve a ser visible
   */
  private resumeAudioOnVisible(): void {
    if (this.wasPlayingBeforeHidden && this.currentAudioElement) {
      this.wasPlayingBeforeHidden = false;
      
      this.currentAudioElement.play().catch((error) => {
        // console.error(`[${this.sessionId}] [Audio] Error al reanudar reproducción:`, error);
        // Si falla, continuar con el siguiente audio
        this.isPlayingAudio = false;
        this.currentAudioElement = null;
        if (this.messageQueue.length > 0 && this.audioQueue.length > 0) {
          this.tryProcessNextChunkPair();
        }
      });
    } else if (this.wasPlayingBeforeHidden && !this.currentAudioElement && this.messageQueue.length > 0 && this.audioQueue.length > 0) {
      // Si el audio se perdió pero había pares en cola, continuar con el siguiente
      this.wasPlayingBeforeHidden = false;
      this.tryProcessNextChunkPair();
    }
  }

  /**
   * Obtener el ID de sesión actual
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Construir contexto completo incluyendo system prompt + historial
   */
  private buildFullContext(): string {
    const prompt = this.currentPrompt || '';
    // Documento adjuntado por el profesor (modo aide-prof): se añade al prompt
    // de sistema para que Galia lo use al proponer actividades. No aparece en el
    // chat ni en el historial; se reenvía en el contexto de cada mensaje.
    const docTag = this.teacherDocumentName
      ? `<document_du_professeur nom="${this.teacherDocumentName.replace(/"/g, "'")}">`
      : '<document_du_professeur>';
    const systemContent = this.teacherDocumentText
      ? `${prompt}\n\n${docTag}\n${this.teacherDocumentText}\n</document_du_professeur>`
      : prompt;
    const processedHistory = this.concatenateAssistantMessages(this.chathistory);
    // Los recordatorios internos (tono, forma, trofeos) se acumulan aquí y van
    // AL FINAL DEL SYSTEM, no como turno 'user' — ver más abajo.
    const internalReminders: string[] = [];
    const fullContext: Array<{ role: string; content: string }> = [
      { role: 'system', content: systemContent },
      ...processedHistory
    ];

    // Opción B (hilo único): si hay un recordatorio de tono pendiente (el usuario
    // acaba de cambiar personalidad/funcionalidad/nivel sin reiniciar el hilo),
    // lo pasamos al final del system. ANTES iba como ÚLTIMO turno 'user' para
    // ganarle por recencia al anclaje del historial, y eso se volvió un bug:
    // cuando el mensaje real del estudiante era trivial (« Merci, Galia. »), el
    // modelo respondía AL RECORDATORIO y le enseñaba las instrucciones internas
    // y su nivel MCER (caso real 17/09/2026, lpaba@ en producción). Al final del
    // system la recencia se conserva (es lo último que lee del system, después
    // de <session> y <verification_avant_envoi>), el último turno del hilo vuelve
    // a ser el del estudiante y el texto deja de ser contestable. El prefijo
    // cacheable de Bedrock no se rompe: el corte está en <session>, antes de
    // esta cola. No se guarda en chathistory ni se muestra en la UI.
    if (this.pendingBehaviorReminder) {
      internalReminders.push(this.pendingBehaviorReminder);
    } else {
      // Refuerzo de personalidad en conversaciones largas: el modelo deriva al
      // tono neutro cuando el historial crece (recencia vence al system prompt;
      // feedback Santiago 2026-07-09: "el sarcasmo solo aparece al principio").
      // Mismo mecanismo que el behavior reminder: último turno 'user', no se
      // guarda en chathistory ni se muestra en la UI. Solo modo estudiante.
      const cfg = configService.getConfig();
      const isAideProf = cfg.practiceMode === 'actividades-docente';
      const tone = PERSONALITY_TONE_REMINDERS[(cfg.personalityType || '') as import('../types').PersonalityType];
      const userTurns = processedHistory.filter((m) => m.role === 'user').length;
      const parts: string[] = [];
      const withTone = !isAideProf && !!tone && userTurns >= 4;
      if (withTone) {
        parts.push(
          `Ta personnalité configurée reste ${tone}. Maintiens ce ton dans TA PROCHAINE RÉPONSE et toutes les suivantes, même si tes derniers tours étaient plus neutres.`
        );
      }
      // Loop de revisión 15/09/2026: en hilos largos Galia encadenaba 2-3
      // preguntas y párrafos de 100+ palabras con A1/A2; la regla vive en el
      // system prompt, pero la recencia del historial la diluye. Corrida 19:00:
      // el tope concreto (c6a94df) siguió ignorándose en un hilo de 58 mensajes
      // (balance motivacional de 95 palabras a una A2), así que el recordatorio
      // va con la cifra del nivel y sin depender de que haya tono configurado.
      if (!isAideProf && userTurns >= 4) {
        const lvl = cfg.mcerLevel;
        const cap = lvl === 'A1' ? ' (A1 : 3 phrases maximum, ~25 mots)' : lvl === 'A2' ? ' (A2 : 4 phrases maximum, ~50 mots)' : '';
        parts.push(
          `Forme : UNE seule question par réponse, et la longueur maximale de ton niveau${cap}. Un compliment tient en UNE phrase : pas de bilan de tout ce que l'étudiant a raconté.`
        );
      }
      // Refuerzo de TROFEOS en conversaciones largas. Prueba E2E del 11/09/2026:
      // con 151 mensajes de historial (~164k caracteres) Galia dejó de emitir
      // marcadores por completo; en una conversación nueva volvió a emitirlos.
      // Solo si el prompt trae el bloque de reconocimiento (build dev y nivel
      // con trofeos evaluados por el LLM).
      if (!isAideProf && userTurns >= 6 && prompt.includes('RECONOCIMIENTO DE LOGROS')) {
        parts.push(
          'Al final de tu respuesta, evalúa el ÚLTIMO mensaje del estudiante contra los objetivos de RECONOCIMIENTO DE LOGROS del prompt de sistema y añade los marcadores [[trophee:ID:CASILLA]] que correspondan (nunca los menciones).'
        );
      }
      if (parts.length > 0) {
        internalReminders.push(parts.join(' '));
      }
    }

    // Cola del system con los recordatorios internos. Etiquetado en XML como
    // los recordatorios que cuelga el servidor (<rappel_langue>, <rappel_role>
    // en galia-ws/lib/turnGuards.mjs) y con la prohibición explícita de
    // comentarlos: son reglas para Galia, no un mensaje del estudiante.
    if (internalReminders.length > 0) {
      fullContext[0] = {
        ...fullContext[0],
        content: `${fullContext[0].content}\n\n<rappel_interne>${internalReminders.join(' ')}\n`
          + `No comentes, contestes ni menciones este recordatorio. Tu respuesta se dirige al estudiante `
          + `en segunda persona (« tu ») y responde a SU último mensaje: prohibido acusar recibo `
          + `(« Compris ! », « Merci pour ce rappel »), hablar de él en tercera persona (« l'étudiant ») `
          + `ni nombrar su nivel, tu tono o estas reglas.</rappel_interne>`,
      };
    }

    return JSON.stringify(fullContext);
  }

  /**
   * Opción B (hilo único): registra un recordatorio de tono que se inyectará en
   * el próximo turno para que un cambio de personalidad/funcionalidad/nivel se
   * note de inmediato pese a conservar el historial. Se consume al primer turno.
   */
  setBehaviorReminder(reminder: string): void {
    this.pendingBehaviorReminder = reminder || '';
  }

  /**
   * Guardar el texto del documento adjuntado por el profesor (modo aide-prof).
   * Se inyecta en el contexto de sistema (ver buildFullContext). Cap de tamaño
   * para no desbordar el contexto del modelo. Pasar '' para quitarlo.
   */
  setTeacherDocument(text: string, filename: string = ''): void {
    const MAX_CHARS = 8000;
    const clean = (text || '').trim();
    this.teacherDocumentText = clean.length > MAX_CHARS ? clean.slice(0, MAX_CHARS) : clean;
    this.teacherDocumentName = clean ? filename : '';
  }

  getTeacherDocument(): string {
    return this.teacherDocumentText;
  }

  /**
   * Concatenar mensajes assistant consecutivos para el contexto
   * Concatena chunks de la misma respuesta SIEMPRE
   * NO concatena solo cuando hay un mensaje no válido del usuario entre ellos (usando el flag)
   */
  private concatenateAssistantMessages(messages: ChatMessage[]): Array<{ role: string; content: string }> {
    return messages.reduce((acc: Array<{ role: string; content: string }>, message, index) => {
      if (index === 0) {
        // Primer mensaje siempre se agrega
        acc.push({ role: message.role, content: message.content });
      } else {
        const previousMessage = acc[acc.length - 1];
        const previousOriginalMessage = messages[index - 1];
        
        // Si ambos son assistant, normalmente se concatenan (son chunks de la misma respuesta)
        if (message.role === 'assistant' && previousMessage.role === 'assistant') {
          // Si el flag está activo Y son consecutivos en el original, significa que hubo un mensaje no válido entre ellos
          // Son respuestas diferentes, NO concatenar
          if (this.lastUserMessageWasEmpty && previousOriginalMessage.role === 'assistant') {
            // NO concatenar: son respuestas diferentes después de un mensaje no válido
            acc.push({ role: message.role, content: message.content });
            // Resetear flag después de procesar el primer mensaje
            this.lastUserMessageWasEmpty = false;
          } else {
            // Concatenar normalmente (chunks de la misma respuesta)
            previousMessage.content = `${previousMessage.content}\n\n${message.content}`;
          }
        } else {
          // Si es user o es el primer assistant después de un user, agregar normalmente
          acc.push({ role: message.role, content: message.content });
        }
      }
      return acc;
    }, []);
  }

  /**
   * Inicializar conexión WebSocket
   */
  async initialize(): Promise<void> {
    // Protección contra múltiples inicializaciones
    if (this.isInitializing || this.isConnecting) {
      return;
    }

    // El prompt que viaja en cada mensaje (currentPrompt) se fija al ABRIR la
    // conexión. Antes, con el socket abierto se devolvía temprano aunque la
    // configuración hubiera cambiado: en la prueba del 11/09/2026 el panel
    // llevaba días abierto, pasó por la puerta de correo (solo cambia la vista)
    // y la conexión vieja siguió viva → Galia evaluaba trofeos con el catálogo
    // B1 estando el estudiante en A2. Ahora, si la firma cambió, se reconecta.
    const liveKey = this.promptKeyFor(configService.getConfig());
    const socketAlive =
      this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING;
    if (socketAlive) {
      if (this.currentPromptKey === liveKey && configService.getConfig().fullPrompt) {
        return;
      }
      logger.log('[WS] Configuración distinta con la conexión viva → reconectar', {
        from: this.currentPromptKey,
        to: liveKey,
      });
      // disconnect() borra el callback de "conexión lista" (lo registró
      // useWebSocket justo antes de llamar a initialize) y el handler de
      // visibilidad: se conservan/re-registran para no dejar la UI colgada.
      const readyCallback = this.onConnectionReadyCallback;
      this.disconnect();
      this.onConnectionReadyCallback = readyCallback;
      this.setupVisibilityHandlers();
    }
    // Prompt en caché armado con otra configuración (p. ej. el nivel cambió vía
    // loadPersonalizationFromCache sin invalidar): forzar reensamblado.
    if (this.currentPromptKey !== liveKey) {
      configService.invalidateFullPrompt();
    }

    this.isInitializing = true;
    this.isConnecting = true;

    try {
      // WebSocket galia-ws: obtener JWT de nuestro endpoint
      const configForAuth = configService.getConfig();
      const email = configForAuth.email || '';
      const fullName = configForAuth.userFullName || '';
      const firstname = configForAuth.userFirstname || '';
      const lastname = configForAuth.userLastname || '';

      // El rol (teacher/student) lo decide el backend: teacher solo con sesión
      // LTI firmada + dominio institucional (fase 4 del plan de seguridad LTI).
      // Se adjunta el token de sesión si existe; sin él, el token sale student.
      const sessionToken = sessionService.getSessionToken();
      const tokenRes = await fetch('/api/ws/generate-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
        },
        body: JSON.stringify({ email, fullName, firstname, lastname }),
      });
      const tokenData = await tokenRes.json();
      if (!tokenData.wsToken) throw new Error('No se pudo obtener token para WebSocket');

      const finalCredentials = {
        accessToken: tokenData.wsToken,
        toConnect: 'alianzafrancesa',
        openaiKey: '',
        anthropicKey: '',
      };

      const config = configService.getConfig();

      // Configurar modelos disponibles
      const availableModels = config.testingPhase
        ? [
            ['gemini', 'gemini-2.0-flash'],
            ['gpt', 'gpt-4o-2024-08-06'],
            ['claude', 'claude-3-sonnet-20240229'],
          ]
        : [
            ['gpt', 'gpt-4o-2024-08-06'],
            ['gpt', 'gpt-3.5-turbo'],
            ['claude', 'claude-3-sonnet-20240229'],
          ];

      const LLMsJson = JSON.stringify(availableModels);
      const transcriptionModel = 'gemini-2.0-flash';

      // Obtener prompt principal
      // IMPORTANTE: Obtener el promptReference directamente del configService para asegurar que esté actualizado
      // Esto es crítico porque el promptReference puede cambiar entre el inicio de initialize() y aquí
      const currentConfig = configService.getConfig();
      const promptReference = currentConfig.promptReference;

      if (!currentConfig.fullPrompt) {
        // Obtener eventos actuales de la Alianza Francesa (S3) para el prompt
        const EVENTOS_AF_URL = 'https://alianza-eventos-767968023146.s3.us-east-1.amazonaws.com/eventos-actuales.json';
        let eventos_obtenidos = '';
        try {
          const res = await fetch(EVENTOS_AF_URL);
          if (res.ok) {
            const data = await res.json();
            eventos_obtenidos = typeof data.eventos !== 'undefined'
              ? JSON.stringify(data.eventos)
              : JSON.stringify(data);
          }
        } catch {
          // Continuar sin eventos si falla la petición
        }

        // Obtener city y role del usuario desde Apolearn
        const apolearnData = authService.getApolearnUserData();
        const userCity = (apolearnData?.user as any)?.city || '';
        const userRole = apolearnData?.role || '';

        // Ensamblar prompt localmente desde prompts.ts (ya no se llama a la API de prompts)
        const dynamicContext: import('../config/prompts').DynamicPromptContext = {
          userFullName: currentConfig.userFullName || '',
          userCity,
          userRole,
          colombiaTime: new Date(),
          eventosAF: eventos_obtenidos,
          summaryPrevAssistant: '',
        };

        const personalityType = (currentConfig.personalityType || 'Amigable') as import('../types').PersonalityType;
        const functionalityType = (currentConfig.functionalityType || 'Conversadora') as import('../types').FunctionalityType;
        const mcerLevel = (currentConfig.mcerLevel || 'B1') as import('../types').MCERLevel;
        const accent = (currentConfig.accent || 'Francia') as import('../types').AccentType;

        // MODO PROFESOR (aide-prof): Galia es asistente pedagógica del PROFESOR,
        // no practica francés con un estudiante. Usa un prompt dedicado e
        // independiente del de estudiante (sin inmersión ni correcciones por nivel).
        const isAideProf = currentConfig.practiceMode === 'actividades-docente';

        let fullPrompt = isAideProf
          ? assembleAideProfPrompt(dynamicContext, mcerLevel)
          : assembleFullPrompt(dynamicContext, personalityType, functionalityType, mcerLevel, accent);

        // Si hay modo académico activo, obtener progreso y agregar contexto de tarea
        let objectivesProgress: any[] = [];
        if (this.academicMode.isActive && this.academicMode.objectives && this.academicMode.userEmail) {
          const { level, unit, lesson, objectives, userEmail } = this.academicMode;
          
          try {
            const progress = await academicService.getUserLessonProgress(
              userEmail,
              level!,
              unit!,
              lesson!
            );
            
            if (progress && progress.objectives_status) {
              objectivesProgress = Object.entries(progress.objectives_status).map(([description, status]: [string, any]) => ({
                description,
                completed: status.completed || false,
                correct_count: status.correct_count || 0,
                total_correct: status.total_correct || 0,
              }));
            }
          } catch (error) {
            // Continuar sin progreso si hay error
          }
          
          // Concatenar contexto de tarea personalizada si existe
          if (this.academicMode.personalizedTask) {
            let objectivesStatusText = '';
            if (objectivesProgress.length > 0) {
              const completedObjectives = objectivesProgress
                .filter((obj: any) => obj.completed)
                .map((obj: any) => obj.description);
              const pendingObjectives = objectivesProgress
                .filter((obj: any) => !obj.completed)
                .map((obj: any) => obj.description);
              
              if (completedObjectives.length > 0) {
                objectivesStatusText += `\n\nOBJETIVOS YA CUMPLIDOS (no te enfoques en estos, ya fueron completados):\n${completedObjectives.map((desc: string, idx: number) => `${idx + 1}. ${desc}`).join('\n')}`;
              }
              
              if (pendingObjectives.length > 0) {
                objectivesStatusText += `\n\nOBJETIVOS PENDIENTES (enfócate en estos durante la conversación):\n${pendingObjectives.map((desc: string, idx: number) => `${idx + 1}. ${desc}`).join('\n')}`;
              }
            }
            
            const taskInstructions = `

---

## CONTEXTO DE LA ACTIVIDAD ACADÉMICA

### TAREA (Situación a Simular):
El estudiante debe realizar la siguiente tarea/situación:
${this.academicMode.personalizedTask}

### OBJETIVOS (Lo que debe cumplir):
Los objetivos son los criterios específicos que el estudiante debe demostrar durante la conversación. ${objectivesStatusText ? `\n${objectivesStatusText}` : ''}

**IMPORTANTE - Diferencia entre TAREA y OBJETIVOS:**
- **TAREA**: Es la situación o escenario que el estudiante debe simular. Es el contexto general de la conversación.
- **OBJETIVOS**: Son los criterios específicos que el estudiante debe cumplir durante la conversación. El estudiante completa la tarea cuando cumple TODOS los objetivos.

**COMPLETACIÓN DE LA ACTIVIDAD:**
La actividad se considera completada cuando el estudiante cumple TODOS los objetivos durante la ejecución de la tarea.

INSTRUCCIONES CRÍTICAS:
1. Si la tarea no lo especifica, inicia con proponerle al estudiante una forma dinámica de cumplir con la tarea (puede ser un juego de rol, una situación, etc.)
2. Si se trata de un juego de rol: debe ser natural y únicamente en francés. No des explicaciones ni correcciones durante el juego de rol. El usuario se encarga de decirte cuándo detenerse. No coloques viñetas, ni paréntesis ni indicaciones.
3. ENFÓCATE PRINCIPALMENTE EN LOS OBJETIVOS PENDIENTES. Si un objetivo ya fue cumplido, no necesitas volver a trabajarlo.

Recuerda: La tarea es el contexto/situación, pero los objetivos son lo que realmente debe cumplir el estudiante.`;

            fullPrompt = fullPrompt + taskInstructions;
          }
        }

        configService.setFullPrompt(fullPrompt);
      }

      // Obtener config actualizado después de obtener el prompt
      const updatedConfig = configService.getConfig();
      if (!updatedConfig.fullPrompt || updatedConfig.fullPrompt.trim() === '') {
        throw new Error('No se pudo obtener el prompt completo');
      }

      // VALIDACIÓN FINAL: Verificar token una vez más antes de conectar
      if (!finalCredentials.accessToken || finalCredentials.accessToken.trim() === '') {
        throw new Error('Token de acceso no disponible al momento de conectar WebSocket');
      }

      // Obtener configuración de voz según modo y acento
      const practiceMode = updatedConfig.practiceMode as PracticeMode || 'conversacional';
      const accent = updatedConfig.accent as AccentType || 'Francia';
      const voiceConfig = getVoiceConfig(practiceMode, accent);

      // Ritmo de habla TTS por nivel MCER. Sprint Galia 1.5 (Santiago, 14-may):
      // La "lentitud" para A1/A2 NO se logra bajando speakingRate (eso distorsiona
      // los fonemas y suena artificial). Se logra inyectando pausas SSML entre
      // palabras/frases en el backend (ver `level` query param → tts.mjs).
      // speakingRate queda cerca de 1.0 para preservar el sonido natural.
      const SPEAKING_RATES: Record<string, number> = {
        // Sprint Galia 1.5 (Santiago, 27-may): C2 sentia lento; subir C1/C2 sin
        // romper la naturalidad. A1/A2 estan validados como OK.
        A1: 0.95, A2: 0.97, B1: 1.00, B2: 1.02, C1: 1.07, C2: 1.12,
      };
      const speakingRate = SPEAKING_RATES[updatedConfig.mcerLevel || 'B1'] ?? 1.0;

      // Inicializar WebSocket
      // Nota: En producción, esto se haría a través de JavaScript externo
      // Por ahora, simulamos la conexión
      this.connectWebSocket({
        tokenUrl: finalCredentials.accessToken,
        toConnect: finalCredentials.toConnect || '',
        prompt: updatedConfig.fullPrompt,
        voice: voiceConfig.voice,
        lenguaje: voiceConfig.language,
        LLMsJson,
        transcriptionModel,
        testingWebSocket: updatedConfig.testingWebSocket,
        speakingRate,
      });
    } catch (error) {
      // console.error('Error al inicializar WebSocket:', error);
      this.isConnecting = false;
      this.isInitializing = false;
      throw error;
    } finally {
      // Asegurar que isInitializing se resetee después de la inicialización
      // (pero mantener isConnecting si la conexión está en progreso)
      if (this.ws?.readyState !== WebSocket.CONNECTING) {
        this.isInitializing = false;
      }
    }
  }

  private connectWebSocket(config: WebSocketConfig): void {
    const { tokenUrl, toConnect, prompt, voice, lenguaje, LLMsJson, transcriptionModel, testingWebSocket, speakingRate } = config;
    
    // Guardar el prompt para poder enviarlo como mensaje inicial después
    this.currentPrompt = prompt;
    this.currentPromptKey = this.promptKeyFor(configService.getConfig());
    // Cada sesión nueva arranca sin documento del profesor (se adjunta luego).
    this.teacherDocumentText = '';
    this.teacherDocumentName = '';
    
    // VALIDACIÓN FINAL: Asegurar que tenemos el token antes de conectar
    if (!tokenUrl) {
      this.isConnecting = false;
      throw new Error('Token de acceso no disponible para conectar WebSocket (undefined/null)');
    }

    if (tokenUrl.trim() === '') {
      this.isConnecting = false;
      throw new Error('Token de acceso no disponible para conectar WebSocket (vacío)');
    }
    
    // Parsear LLMs
    const LLMs = JSON.parse(LLMsJson);
    this.mainLLM = LLMs[0][0];
    this.mainModel = LLMs[0][1];
    this.backupLLM = LLMs[1] ? LLMs[1][0] : LLMs[0][0];
    this.backupModel = LLMs[1] ? LLMs[1][1] : LLMs[0][1];
    this.transcriptionModel = transcriptionModel || '';
    
    // Obtener configuración para determinar si usar servidor de desarrollo o producción
    // Si testingPhase es false (producción), siempre usar servidor de producción
    // independientemente del valor de testingWebSocket
    const appConfig = configService.getConfig();
    const useDevServer = appConfig.testingPhase && testingWebSocket;
    
    // Construir URL del WebSocket con parámetros codificados
    const whisperPrompt = 'Umm, Amm, Emm, hmm... Okay, Ahh, Ehh, Ohh, Galia, Comment ça va';
    const baseUrl = useDevServer
      ? (import.meta.env.VITE_WS_URL_DEV || 'wss://ws-galia-dev.aitopstaff.com')
      : (import.meta.env.VITE_WS_URL_PRO || 'wss://ws-galia.aitopstaff.com');

    const params = new URLSearchParams({
      token: tokenUrl,
      lang: lenguaje,
      name: voice,
      tenant: toConnect || '',
      environment: useDevServer ? 'DEV' : 'PRO',
      stt_service: 'llm',
      whisper_prompt: whisperPrompt,
      rate: String(speakingRate ?? 1.0),
      level: configService.getConfig().mcerLevel || 'B1',
    });

    const wsUrl = `${baseUrl}?${params.toString()}`;

    logger.log('[WS-DIAG] Abriendo WebSocket', { baseUrl, useDevServer, speakingRate, tokenPresent: !!tokenUrl, tokenLen: tokenUrl?.length });

    this.ws = new WebSocket(wsUrl);
    
    this.ws.onopen = () => {
      // Verificar que el WebSocket esté realmente abierto
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // Procesar cola de mensajes pendientes
        if (this.pendingSendQueue.length > 0) {
          const queueCopy = [...this.pendingSendQueue];
          this.pendingSendQueue = [];
          
          queueCopy.forEach(({ payload: queuedPayload }) => {
            this.ws!.send(queuedPayload);
          });
        }
        
        // Heartbeat cada 30 segundos
        this.heartbeatInterval = setInterval(() => {
          if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({
              message: 'heartbeat',
              timestamp: new Date().toLocaleString(),
            }));
          }
        }, 30000);
        
        this.isConnecting = false;
        this.isInitializing = false;
        this.reconnectAttempts = 0;
        
        // Saludo automático deshabilitado a pedido de Bryan + Santiago (2026-05-27):
        // antes Galia abría la sesión con un mensaje fijo ("Bonjour X, ravie de te
        // retrouver…") que siempre era el mismo por estudiante. Ahora es el usuario
        // quien inicia. El backend detecta el primer turno real por el contexto
        // (solo role=system, sin user/assistant previos) y carga la memoria ahí.
        logger.log('[WS-DIAG] WS onopen — promptLen:', this.currentPrompt?.length || 0, '(sin saludo automático)');
        
        // Notificar que la conexión está lista
        if (this.onConnectionReadyCallback) {
          this.onConnectionReadyCallback();
        }
      }
    };
    
    this.ws.onmessage = (event) => {
      if (!this.canSendMessage) return;
      
      // Ignorar mensajes mientras se están limpiando las colas
      if (this.isClearingQueues) {
        return;
      }
      
      if (typeof event.data === 'string' && event.data !== 'Heartbeat received') {
        // Reconocimientos de trofeos (backend los extrae de los marcadores
        // [[trophee:ID:CASILLA]] del LLM y los manda como mensaje de control
        // ANTES del end marker). Se interceptan aquí para que JAMÁS entren a
        // las colas texto/audio; App.tsx escucha el evento y suma contadores
        // en achievementService (necesita el email, que vive en su scope).
        if (event.data.startsWith('__TROPHEES__')) {
          try {
            const items = JSON.parse(event.data.slice('__TROPHEES__'.length));
            if (Array.isArray(items) && items.length > 0) {
              window.dispatchEvent(
                new CustomEvent('galia:trophees-reconnus', { detail: items })
              );
            }
          } catch {
            /* payload malformado: ignorar */
          }
          return;
        }
        // Marker explicito de fin de respuesta (backend galia-ws lo emite tras
        // streamLLMResponse). Antes el cliente infieria fin con grace period
        // tras el ultimo audio onended — si el ultimo chunk se atrasaba mas que
        // el grace, finishResponse disparaba prematuro y la burbuja quedaba
        // cortada (feedback Santi 2026-05-29 "Et d'ailleurs,"). Ahora finalizamos
        // exacto cuando el backend lo indica.
        if (event.data === '__END_OF_RESPONSE__') {
          this.responseEndMarkerReceived = true;
          this.cancelPendingFinish();
          // Si las colas ya estan vacias y no hay audio en curso, finalizamos ya.
          // Si hay chunks pendientes, audio.onended del ultimo chunk los procesa
          // y finalmente llamara a finishResponse (sin grace, gracias al flag).
          if (!this.isPlayingAudio && this.messageQueue.length === 0 && this.audioQueue.length === 0) {
            this.finishResponse();
          }
          return;
        }
        // Manejar errores LLM
        if (event.data === 'ErrorLLM01') {
          this.handleLLMError(true);
        } else if (event.data === 'ErrorLLM02') {
          this.handleLLMError(false);
        } else {
          // Detectar si es transcripción o respuesta del LLM
          if (event.data.includes('"role": "user", "content":') || 
              event.data.includes('"role":"user","content":') ||
              (event.data.includes('"role"') && event.data.includes('"user"') && event.data.includes('"content"'))) {
            
            // Solo procesar como transcripción si estábamos esperando una (audio enviado)
            if (this.expectingTranscription) {
              // Es la transcripción de un mensaje de audio
              try {
                const message = JSON.parse(event.data);
                // Detectar mensajes no válidos: "(non reconnu)" o similar
                const isNotRecognized = message.content && (
                  message.content.includes('(non reconnu)') ||
                  message.content.toLowerCase().includes('not recognized') ||
                  message.content.toLowerCase().includes('no reconocido')
                );
                
                if (message.content && !isNotRecognized) {
                  this.processTranscription(message.content);
                  this.expectingTranscription = false;
                  // STT entendió: cerrar el indicador "Transcribiendo…"
                  if (this.onTranscriptionStateCallback) {
                    this.onTranscriptionStateCallback('done');
                  }
                } else {
                  // Mensaje no válido: activar flag y contar
                  this.lastUserMessageWasEmpty = true;
                  conversationSaverService.incrementNotValidCount(); // Contar mensaje no válido
                  this.expectingTranscription = false;
                  // STT no entendió: avisar a la UI para mostrar "no te entendí"
                  if (this.onTranscriptionStateCallback) {
                    this.onTranscriptionStateCallback('not_recognized');
                  }
                }
              } catch (e) {
                // console.error(`[${this.sessionId}] [WebSocket] Error al parsear transcripción:`, e, 'Datos:', event.data);
                this.expectingTranscription = false;
                // Parse fallido: tratarlo como no reconocido para no dejar el
                // indicador "Transcribiendo…" colgado para siempre.
                if (this.onTranscriptionStateCallback) {
                  this.onTranscriptionStateCallback('not_recognized');
                }
              }
            }
          } else {
            // Es un chunk de respuesta del LLM
            this.processTextChunk(event.data);
          }
        }
      } else if (typeof event.data === 'object') {
        // Ignorar audio mientras se están limpiando las colas
        if (this.isClearingQueues) {
          return;
        }
        
        // Audio de respuesta - Encolar e intentar procesar par
        // Si la respuesta fue interrumpida, descartar
        if (this.isResponseInterrupted) {
          const audioBlob = new Blob([event.data], { type: 'audio/mp3' });
          this.interruptedAudioChunks.push(audioBlob);
          return;
        }
        
        const audioBlob = new Blob([event.data], { type: 'audio/mp3' });
        const audioURL = URL.createObjectURL(audioBlob);

        // Llego audio nuevo: cancelar el finish pendiente si lo habia
        this.cancelPendingFinish();

        // Encolar audio
        this.audioQueue.push({ url: audioURL, blob: audioBlob });

        // Intentar procesar siguiente par si ambos están disponibles
        this.tryProcessNextChunkPair();
      }
    };
    
    this.ws.onclose = (event) => {
      console.warn('[WS-DIAG] WS cerrado', { code: event.code, reason: event.reason, wasClean: event.wasClean });
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }
      
      // Intentar reconectar si:
      // 1. No fue un cierre limpio (desconexión inesperada)
      // 2. O si fue un cierre limpio pero la página está visible (puede ser reconexión necesaria)
      const shouldReconnect = (!event.wasClean || (event.wasClean && !document.hidden)) && 
                              this.reconnectAttempts < this.maxReconnectAttempts;
      
      if (shouldReconnect) {
        this.reconnect();
      } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        // console.error(`[${this.sessionId}] [WebSocket] Máximo de intentos de reconexión alcanzado`);
      }
    };
    
    this.ws.onerror = (error) => {
      console.error('[WS-DIAG] Error en conexión WS', error, 'readyState:', this.ws?.readyState);
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }
      this.isConnecting = false;
    };
  }
  
  /**
   * Manejar errores LLM y reintentar
   */
  private handleLLMError(useBackup: boolean): void {
    const encodeBase64 = btoa('starting');
    // Construir contexto completo con system prompt + historial
    const fullContext = this.buildFullContext();
    
    const payload = JSON.stringify({
      context: fullContext,
      audio: encodeBase64,
      llm: useBackup ? this.backupLLM : this.mainLLM,
      model: useBackup ? this.backupModel : this.mainModel,
    });
    
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(payload);
    }
  }

  /**
   * Interrumpir la respuesta actual del asistente
   * - Detiene audio inmediatamente
   * - MANTIENE el texto visible en el chat
   * - Descarta chunks que sigan llegando
   */
  private interruptCurrentResponse(): void {
    // 1. Detener audio inmediatamente
    if (this.currentAudioElement) {
      this.currentAudioElement.pause();
      this.currentAudioElement = null;
    }
    this.isPlayingAudio = false;
    this.wasPlayingBeforeHidden = false;
    
    // Cancelar finishResponse pendiente — la interrupcion gana
    this.cancelPendingFinish();
    this.responseEndMarkerReceived = false;

    // 2. Activar flag de interrupción para descartar chunks siguientes
    this.isResponseInterrupted = true;
    this.interruptedResponseChunks = [];
    this.interruptedAudioChunks = [];
    
    // 3. MANTENER el texto visible (marcar como completo aunque esté incompleto)
    const lastMessage = this.chathistory[this.chathistory.length - 1];
    if (lastMessage && lastMessage.role === 'assistant') {
      // Si no tiene timestamp, agregarlo para hacerlo visible
      if (!lastMessage.timestamp) {
        lastMessage.timestamp = Date.now();
      }
    }
    
    // 4. Desactivar avatar
    if (this.onAvatarStateCallback) {
      this.onAvatarStateCallback(false);
    }
    
    // 5. Limpiar AMBAS colas (texto y audio pendientes)
    if (this.messageQueue.length > 0 || this.audioQueue.length > 0) {
      this.messageQueue = [];
      this.audioQueue.forEach(item => URL.revokeObjectURL(item.url));
      this.audioQueue = [];
    }
  }

  /**
   * Limpiar completamente una respuesta interrumpida
   * FASE 2: Se ejecuta cuando detectamos que la respuesta interrumpida terminó
   */
  private cleanupInterruptedResponse(): void {
    // 1. Limpiar cola de audio pendiente
    if (this.audioQueue.length > 0) {
      this.audioQueue.forEach(item => URL.revokeObjectURL(item.url));
      this.audioQueue = [];
    }
    
    // 2. Limpiar chunks de texto acumulados
    if (this.accumulatedChunks.length > 0) {
      this.accumulatedChunks = '';
    }
    
    // 3. Descartar chunks interrumpidos acumulados
    this.interruptedResponseChunks = [];
    this.interruptedAudioChunks = [];
    
    // 4. Eliminar el último mensaje del asistente si está incompleto
    const lastMessage = this.chathistory[this.chathistory.length - 1];
    if (lastMessage && lastMessage.role === 'assistant') {
      this.chathistory.pop();
      
      // Notificar callback para actualizar la UI
      if (this.onMessageCallback && this.chathistory.length > 0) {
        const newLastMessage = this.chathistory[this.chathistory.length - 1];
        this.onMessageCallback(newLastMessage);
      }
    }
    
    // 5. Resetear flags
    this.isResponseInterrupted = false;
  }

  /**
   * Enviar mensaje de audio
   */
  async sendAudioMessage(audioBlob: Blob, inputType: string = 'audio'): Promise<void> {
    try {
      // Convertir audio a base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Audio = (reader.result as string).split(',')[1];
        this.sendMessage(base64Audio, inputType);
      };
      reader.readAsDataURL(audioBlob);
    } catch (error) {
      console.error('Error al enviar mensaje de audio:', error);
      throw error;
    }
  }

  /**
   * Enviar mensaje de texto
   */
  async sendTextMessage(text: string): Promise<void> {const trimmedText = text.trim();
    if (!trimmedText || trimmedText.length === 0) {
      // Mensaje vacío: activar flag pero no agregar al historial
      // Aún así enviar al WebSocket para que el asistente responda
      this.lastUserMessageWasEmpty = true;
      conversationSaverService.incrementNotValidCount(); // Contar mensaje no válidothis.sendMessage('', 'text');
      return;
    }
    this.lastUserMessageWasEmpty = false;this.sendMessage(trimmedText, 'text');
  }

  private sendMessage(content: string, inputType: string): void {
      // INTERRUPCIÓN: Cuando el usuario envía un nuevo mensaje, interrumpir la respuesta actual
      this.interruptCurrentResponse();
      
      // Resetear flag de interrupción ANTES de enviar el nuevo mensaje
      // para que la nueva respuesta no se descarte
      this.isResponseInterrupted = false;
      
      // Resetear accumulatedChunks para la nueva respuesta
      // Esto previene que chunks de respuestas anteriores se acumulen
      this.accumulatedChunks = '';
      
      if (inputType === 'audio') {
      // El audio base64 NO se guarda en chathistory
      // Solo se envía al WebSocket para procesamiento con Gemini
      // El texto transcrito llegará después vía processTranscription
      this.expectingTranscription = true; // Marcar que esperamos transcripción

      // Avisar a la UI que arrancó la transcripción (muestra "Transcribiendo…")
      if (this.onTranscriptionStateCallback) {
        this.onTranscriptionStateCallback('transcribing');
      }

      // Construir contexto completo con system prompt + historial
      const fullContext = this.buildFullContext();
      
      const payload = JSON.stringify({
        context: fullContext,
        entry_format: 'audio',
        audio: content,
        model: this.mainModel,
        llm: this.mainLLM,
        llm_transcription_model: this.transcriptionModel,
        audio_enabled: this.audioEnabled,
      });
      
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(payload);
        // NO registrar interacción aquí - se cuenta por conversación, no por mensaje
      } else {
        // console.warn(`[${this.sessionId}] [Mensaje Audio] WebSocket no está listo (estado: ${this.ws?.readyState}). Encolando mensaje...`);
        this.pendingSendQueue.push({ payload, type: 'audio' });
      }
    } else {
      // Los mensajes de texto se guardan en chathistory solo si no están vacíos
      // Si el flag lastUserMessageWasEmpty está activo, no agregar al historial
      if (!this.lastUserMessageWasEmpty && content && content.trim().length > 0) {
        const message: ChatMessage = {
          role: 'user',
          // Loop 22/09/2026 (fnd_b2cbf5e716): tres turnos llegaron a Supabase
          // con el prefijo repetido (« [text] [audio] … », « [text] [text]
          // [audio] … » en nmarevalo1@ y jbcastaneda@) porque el contenido que
          // se reenvía YA traía uno. Se quita el que venga antes de poner el
          // nuestro: nunca se acumula.
          content: '[text] ' + stripChannelPrefix(content),
          timestamp: Date.now(),
        };

        this.chathistory.push(message);
        
        // Detener contador de tiempo muerto cuando el usuario habla
        conversationSaverService.stopIdleTimer();
        
      // Notificar callback inmediatamente para actualizar la UI
      if (this.onMessageCallback) {
        this.onMessageCallback(message);
      }

      // Evaluar mensaje para objetivos académicos si está activo
      if (this.academicMode.isActive) {this.evaluateUserMessageForObjectives(content).catch((err) => { /* console.error(err); */ });
      }
      }
      
      // Construir contexto completo con system prompt + historial
      const fullContext = this.buildFullContext();
      
      const payload = JSON.stringify({
        context: fullContext,
        entry_format: 'text',
        text: content,
        audio: '',
        model: this.mainModel,
        llm: this.mainLLM,
        llm_transcription_model: this.transcriptionModel,
        audio_enabled: this.audioEnabled,
      });
      
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {this.ws.send(payload);
        // NO registrar interacción aquí - se cuenta por conversación, no por mensaje
      } else {
        // console.warn(`[${this.sessionId}] [Mensaje Texto] WebSocket no está listo (estado: ${this.ws?.readyState}). Encolando mensaje...`);
        this.pendingSendQueue.push({ payload, type: 'text' });
      }
    }

    // Opción B: el recordatorio de tono (si lo había) ya quedó incrustado en el
    // payload vía buildFullContext. Lo consumimos para que aplique SOLO al primer
    // turno tras el cambio de configuración y no se repita en cada mensaje.
    if (this.pendingBehaviorReminder) {
      this.pendingBehaviorReminder = '';
    }
  }

  /**
   * Activar modo académico (solo guarda el estado, no cambia prompt reference)
   */
  setAcademicMode(
    level: string,
    unit: number,
    lesson: number,
    objectives: LessonObjective[],
    userEmail: string,
    onObjectiveUpdate?: (objectiveDescription: string, isCorrect: boolean) => void,
    personalizedTask?: string,
    onEvaluationComplete?: () => void
  ): void {
    this.academicMode = {
      isActive: true,
      level,
      unit,
      lesson,
      objectives,
      personalizedTask,
      userEmail,
      onObjectiveUpdate,
      onEvaluationComplete,
    };
  }

  /**
   * Iniciar actividad de lección: cambiar prompt reference a ruta académica y reconectar WebSocket.
   * Galia conversacional solo se usa en la inicialización normal de la app.
   */
  async startLessonActivity(): Promise<void> {
    if (!this.academicMode.isActive || !this.academicMode.objectives || !this.academicMode.userEmail) {
      // console.warn('[WebSocket] No se puede iniciar actividad: modo académico no activo o incompleto');
      return;
    }

    // 1. Guardar conversación actual en Lambda antes de cambiar el prompt reference
    // Seguir la misma lógica que cuando se cambia la configuración en el menú
    const currentMessages = this.getChatHistory();
    
    if (currentMessages.length > 0) {
      const finalMessages = currentMessages.map((m) => ({
        role: m.role,
        content: m.content,
        afterInvalidMessage: m.afterInvalidMessage,
      }));
      
      try {
        await conversationSaverService.guardarData(finalMessages);
      } catch (error) {
        // console.error('[WebSocket] ❌ Error al guardar conversación anterior:', error);
        // Continuar aunque haya error al guardar
      }
    }

    // 2. Limpiar todas las colas de audio y mensajes pendientes
    this.clearAllQueues();

    // 3. Limpiar historial de chat y resetear estado
    this.clearChatHistory(); // Usar clearChatHistory() para que se actualice la UI inmediatamente
    this.lastUserMessageWasEmpty = false;
    this.accumulatedChunks = '';
    this.interruptedResponseChunks = [];
    this.interruptedAudioChunks = [];
    this.isResponseInterrupted = false;
    this.expectingTranscription = false;
    this.sessionId = `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // 4. Resetear contadores en conversationSaverService
    conversationSaverService.resetConversationState();
    
    // 5. Cambiar el prompt reference a la ruta académica (lección/actividad)
    const config = configService.getConfig();
    const isTestingPhase = config.testingPhase;
    const suffix = isTestingPhase ? '-dev' : '-pro';
    const academicRoutePromptReference = `alianzafrancesa-galia-ruta-academica${suffix}`;
    configService.setPromptReference(academicRoutePromptReference);

    // 6. Desconectar WebSocket actual si está conectado para forzar reconexión con nuevo prompt reference
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      this.disconnect();
      
      // Esperar un momento para que la desconexión se complete
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // 7. Reconectar con el contexto académico aplicado
    try {
      await this.initialize();
    } catch (error) {
      throw error;
    }
  }


  /**
   * Reiniciar la conversación con Galia (pedido de Diana Soler, reunión
   * 2026-07-21): el estudiante repite un ejercicio desde cero sin cambiar de
   * configuración. Guarda lo conversado, limpia historial/colas/estado y
   * estrena internal_interaction_id (fila nueva en Supabase). La conexión WS
   * se CONSERVA: el contexto del LLM viaja completo en cada turno (ver
   * buildFullContext), así que vaciar el historial deja a Galia sin ningún
   * recuerdo del hilo anterior — no hace falta reconectar.
   */
  async resetConversation(): Promise<void> {
    // 1. Cortar lo que esté sonando o llegando del turno en curso
    this.interruptPlayback();

    // 2. Persistir la conversación que se cierra (mejor esfuerzo)
    const currentMessages = this.getChatHistory();
    if (currentMessages.length > 0) {
      const finalMessages = currentMessages.map((m) => ({
        role: m.role,
        content: m.content,
        afterInvalidMessage: m.afterInvalidMessage,
      }));
      try {
        await conversationSaverService.guardarData(finalMessages);
      } catch {
        // Continuar aunque falle el guardado: el reinicio no debe bloquearse.
      }
    }

    // 3. Limpiar colas, historial y estado del turno
    this.clearAllQueues();
    this.clearChatHistory();
    this.lastUserMessageWasEmpty = false;
    this.accumulatedChunks = '';
    this.interruptedResponseChunks = [];
    this.interruptedAudioChunks = [];
    this.isResponseInterrupted = false;
    this.expectingTranscription = false;
    this.pendingBehaviorReminder = '';
    this.sessionId = `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // 4. Nueva fila de conversación para lo que venga
    conversationSaverService.resetConversationState();
  }

  /**
   * Cambiar a una conversación reciente (pestañas del chat, acuerdo Santiago
   * 01/09/2026): guarda el hilo actual, limpia colas/estado del turno y
   * rehidrata el historial de la conversación elegida, que sigue escribiéndose
   * en SU fila de Supabase (restoreConversationState). Misma mecánica que
   * resetConversation(): la conexión WS se conserva porque el contexto viaja
   * completo en cada turno.
   */
  async switchConversation(
    messages: ChatMessage[],
    internalInteractionId: string,
    previousTimeTotalSeconds: number,
  ): Promise<void> {
    this.interruptPlayback();

    const currentMessages = this.getChatHistory();
    if (currentMessages.length > 0) {
      const finalMessages = currentMessages.map((m) => ({
        role: m.role,
        content: m.content,
        afterInvalidMessage: m.afterInvalidMessage,
      }));
      try {
        await conversationSaverService.guardarData(finalMessages);
      } catch {
        // Mejor esfuerzo: cambiar de pestaña no debe bloquearse por el guardado.
      }
    }

    this.clearAllQueues();
    this.lastUserMessageWasEmpty = false;
    this.accumulatedChunks = '';
    this.interruptedResponseChunks = [];
    this.interruptedAudioChunks = [];
    this.isResponseInterrupted = false;
    this.expectingTranscription = false;
    this.pendingBehaviorReminder = '';
    this.sessionId = `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Historial de la conversación elegida + su fila en Supabase (los mensajes ya
    // persistidos no se reenvían como nuevos).
    conversationSaverService.restoreConversationState(
      internalInteractionId,
      messages.length,
      previousTimeTotalSeconds,
    );
    this.loadChatHistory(messages);
  }

  /**
   * Desactivar modo académico
   */
  clearAcademicMode(): void {
    this.academicMode = { isActive: false };
  }

  /**
   * Obtener estado actual del modo académico
   */
  getAcademicMode(): { isActive: boolean; level?: string; unit?: number; lesson?: number } {
    return {
      isActive: this.academicMode.isActive,
      level: this.academicMode.level,
      unit: this.academicMode.unit,
      lesson: this.academicMode.lesson
    };
  }

  /**
   * Evaluar mensaje del usuario para detectar cumplimiento de objetivos
   * Usa GPT para evaluar si la frase actual cumple con los objetivos
   */
  private async evaluateUserMessageForObjectives(userMessage: string): Promise<void> {
    if (!this.academicMode.isActive || !this.academicMode.objectives || !this.academicMode.userEmail) {
      return;
    }

    const { level, unit, lesson, objectives, userEmail, onObjectiveUpdate } = this.academicMode;

    // Obtener progreso para pasar frases usadas a GPT (GPT decidirá si descartar repeticiones)
    const progress = await academicService.getUserLessonProgress(userEmail, level!, unit!, lesson!);
    const allUsedPhrases: string[] = [];
    
    if (progress) {
      // Recopilar todas las frases usadas de todos los objetivos
      allUsedPhrases.push(...Object.values(progress.objectives_status)
        .flatMap(status => status.used_phrases || []));
    }

    // Evaluar objetivos usando LLM, pasando la lista de frases usadas
    // LLM decidirá si la frase es repetida y descartará objetivos en consecuencia
    const evaluationResults = await objectiveEvaluationService.evaluateObjectivesWithGPT(
      userMessage,
      objectives
    );

    // Si no hay resultados (error en GPT), usar evaluación básica como fallback
    if (evaluationResults.length === 0) {
      return;
    }

    // El servicio de evaluación ya devuelve los objetivos ordenados por prioridad
    // (objetivos cumplidos con mayor priority_score primero)
    // Simplemente tomamos el primer objetivo cumplido con confianza suficiente
    const topPriorityResult = evaluationResults.find(
      (result: ObjectiveEvaluationResult) => result.is_fulfilled && result.confidence >= 0.5
    );

    // Si no hay objetivos cumplidos, no procesar nada
    if (!topPriorityResult) {
      return;
    }

    // Encontrar el objetivo correspondiente usando la descripción
    const objective = objectives.find(obj => obj.description === topPriorityResult.description);
    if (!objective) {
      return;
    }

    const isCorrect = topPriorityResult.is_fulfilled;
    const confidence = topPriorityResult.confidence;

    // Procesar el objetivo con mayor prioridad (ya viene priorizado del servicio de evaluación)
    if (isCorrect && confidence >= 0.5) {
      // Para objetivos de vocabulario, el servicio de evaluación puede incluir palabras encontradas
      // en el futuro, pero por ahora simplemente pasamos undefined y el servicio académico
      // puede extraerlas dinámicamente si es necesario
      let vocabularyWordsFound: string[] | undefined = undefined;

      // Actualizar progreso del objetivo
      if (onObjectiveUpdate) {
        onObjectiveUpdate(objective.description, isCorrect);
      }

      // Actualizar en base de datos
      logger.log('[WebSocketService] 🔄 Actualizando estado de objetivo:', {
        userEmail,
        level: level!,
        unit: unit!,
        lesson: lesson!,
        objectiveDescription: objective.description,
        isCorrect,
        academicModeLevel: this.academicMode.level,
        academicModeUnit: this.academicMode.unit,
        academicModeLesson: this.academicMode.lesson
      });
      
      const updatedProgress = await academicService.updateObjectiveStatus(
        userEmail,
        level!,
        unit!,
        lesson!,
        objective.description,
        isCorrect,
        vocabularyWordsFound,
        userMessage // Pasar el mensaje para verificar repetición
      );

      // Actualizar el prompt con el progreso actualizado de objetivos para que la IA sepa qué objetivos ya se cumplieron
      await this.updatePromptWithCurrentObjectives();

      // Disparar evaluación cuando todos los objetivos están completos.
      // Usar el progreso recién actualizado evita condiciones de carrera y asegura que
      // en tareas personalizadas (ACTIVIDAD_xxx) la evaluación se ejecute correctamente.
      if (updatedProgress?.all_objectives_completed) {
        await this.evaluateWithRubric();
        return;
      }
    }

    // Fallback: verificar si todos los objetivos están completados (p. ej. si updateObjectiveStatus devolvió null)
    const allCompleted = await academicService.checkAllObjectivesCompleted(
      userEmail,
      level!,
      unit!,
      lesson!
    );

    if (allCompleted) {
      await this.evaluateWithRubric();
    }
  }

  /**
   * Evaluar conversación completa con rúbrica cuando se completan todos los objetivos
   */
  private async evaluateWithRubric(): Promise<void> {
    if (!this.academicMode.isActive || !this.academicMode.userEmail) {
      return;
    }

    const { level, unit, lesson, userEmail } = this.academicMode;

    try {
      // Obtener progreso actual
      const progress = await academicService.getUserLessonProgress(
        userEmail,
        level!,
        unit!,
        lesson!
      );

      if (!progress || !progress.all_objectives_completed) {
        return;
      }

      // Convertir chathistory a formato de conversación
      const conversation = this.chathistory.map(msg => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
      }));

      // Obtener tarea y objetivos del modo académico o del progreso
      // Prioridad: 1) academicMode (sesión actual), 2) progress (guardado)
      const taskDescription = this.academicMode.personalizedTask || progress.personalized_task;
      
      // Obtener objetivos: primero desde academicMode, luego desde objectives_status
      let objectives: Array<{ description: string }> | undefined;
      if (this.academicMode.objectives && this.academicMode.objectives.length > 0) {
        objectives = this.academicMode.objectives.map(obj => ({ description: obj.description }));
      } else if (progress.objectives_status) {
        objectives = Object.keys(progress.objectives_status).map(desc => ({ description: desc }));
      }

      // Evaluar con rúbrica
      const evaluation = await academicEvaluationService.evaluateWithRubric(
        conversation,
        level!,
        unit!,
        lesson!,
        progress.objectives_status,
        taskDescription,
        objectives
      );

      if (evaluation) {
        // Guardar evaluación
        await academicEvaluationService.saveEvaluation(
          userEmail,
          level!,
          unit!,
          lesson!,
          evaluation
        );

        // Notificar que la evaluación se completó para actualizar la interfaz
        const hasCallback = !!this.academicMode.onEvaluationComplete;
        if (this.academicMode.onEvaluationComplete) {
          this.academicMode.onEvaluationComplete();
        } else {
          // console.warn('[WebSocket] ⚠️ No hay callback onEvaluationComplete registrado');
        }
      } else {
        // console.warn('[WebSocket] ⚠️ No se generó evaluación');
      }
    } catch (error) {
      // Error al evaluar con rúbrica
    }
  }

  /**
   * Disparar la evaluación con rúbrica desde la UI cuando todos los objetivos están completos.
   * Útil cuando el flujo interno (evaluateUserMessageForObjectives) no pudo ejecutarla (p. ej. updateObjectiveStatus devolvió null por 406/409).
   */
  async triggerEvaluationIfAllComplete(): Promise<void> {
    if (!this.academicMode.isActive || !this.academicMode.userEmail) {
      return;
    }
    await this.evaluateWithRubric();
  }

  /**
   * Evaluar la calidad pedagógica de la respuesta del asistente
   * Se ejecuta automáticamente cuando el asistente termina de responder
   */
  private async evaluateAssistantResponse(assistantMessage: string): Promise<void> {
    if (!this.academicMode.isActive || !this.academicMode.objectives) {
      return;
    }

    // Obtener el último mensaje del usuario para contexto
    const userMessages = this.chathistory.filter(m => m.role === 'user');
    if (userMessages.length === 0) {
      return; // No hay mensaje del usuario para evaluar
    }

    const lastUserMessage = userMessages[userMessages.length - 1].content;
    
    try {
      const evaluation = await assistantResponseEvaluationService.evaluateAssistantResponse(
        assistantMessage,
        lastUserMessage,
        this.academicMode.objectives
      );

      if (!evaluation) {
        return; // Error en la evaluación
      }

      // Aquí se podría guardar la evaluación en Supabase para analytics
      // o enviar a un endpoint para revisión por desarrolladores
      // Por ahora solo se loguea

    } catch (error) {
      // Error al evaluar respuesta del asistente
    }
  }

  /**
   * Actualizar el prompt con el progreso actualizado de objetivos
   * Se llama cuando se cumple un objetivo para que la IA sepa en tiempo real qué objetivos ya se completaron
   */
  private async updatePromptWithCurrentObjectives(): Promise<void> {
    if (!this.academicMode.isActive || !this.academicMode.userEmail || !this.academicMode.personalizedTask) {
      return;
    }

    const { level, unit, lesson, userEmail } = this.academicMode;

    try {
      // Obtener progreso actualizado
      const progress = await academicService.getUserLessonProgress(
        userEmail,
        level!,
        unit!,
        lesson!
      );

      if (!progress || !progress.objectives_status) {
        return;
      }

      // Reconstruir prompt localmente con progreso actualizado
      const config = configService.getConfig();
      
      const objectivesProgress = Object.entries(progress.objectives_status).map(([description, status]: [string, any]) => ({
        description,
        completed: status.completed || false,
        correct_count: status.correct_count || 0,
        total_correct: status.total_correct || 0,
      }));

      // Obtener datos dinámicos para re-ensamblar
      const apolearnData = authService.getApolearnUserData();
      const dynamicCtx: import('../config/prompts').DynamicPromptContext = {
        userFullName: config.userFullName || '',
        userCity: (apolearnData?.user as any)?.city || '',
        userRole: apolearnData?.role || '',
        colombiaTime: new Date(),
        eventosAF: '',
        summaryPrevAssistant: '',
      };

      const pType = (config.personalityType || 'Amigable') as import('../types').PersonalityType;
      const fType = (config.functionalityType || 'Conversadora') as import('../types').FunctionalityType;
      const mLevel = (config.mcerLevel || 'B1') as import('../types').MCERLevel;
      const acc = (config.accent || 'Francia') as import('../types').AccentType;

      let fullPrompt = assembleFullPrompt(dynamicCtx, pType, fType, mLevel, acc);

      // Construir el texto de estado de objetivos actualizado
      let objectivesStatusText = '';
      const completedObjectives = objectivesProgress
        .filter((obj: any) => obj.completed)
        .map((obj: any) => obj.description);
      const pendingObjectives = objectivesProgress
        .filter((obj: any) => !obj.completed)
        .map((obj: any) => obj.description);
      
      if (completedObjectives.length > 0) {
        objectivesStatusText += `\n\nOBJETIVOS YA CUMPLIDOS (no te enfoques en estos, ya fueron completados):\n${completedObjectives.map((desc: string, idx: number) => `${idx + 1}. ${desc}`).join('\n')}`;
      }
      
      if (pendingObjectives.length > 0) {
        objectivesStatusText += `\n\nOBJETIVOS PENDIENTES (enfócate en estos durante la conversación):\n${pendingObjectives.map((desc: string, idx: number) => `${idx + 1}. ${desc}`).join('\n')}`;
      }

      // Reconstruir las instrucciones de tarea con el estado actualizado
      const taskInstructions = `

---

## CONTEXTO DE LA ACTIVIDAD ACADÉMICA

### TAREA:
El estudiante debe realizar la siguiente tarea:
${this.academicMode.personalizedTask}

### OBJETIVOS (Lo que debe cumplir):
Los objetivos son los criterios específicos que el estudiante debe demostrar durante la conversación. ${objectivesStatusText ? `\n${objectivesStatusText}` : ''}

**IMPORTANTE - Diferencia entre TAREA y OBJETIVOS:**
- **TAREA**: Es el logro que el estudiante debe cumplir. Hace parte del contexto general de la conversación.
- **OBJETIVOS**: Son los criterios específicos que el estudiante debe cumplir durante la conversación. El estudiante completa la tarea cuando cumple TODOS los objetivos.

**COMPLETACIÓN DE LA ACTIVIDAD:**
La actividad se considera completada cuando el estudiante cumple TODOS los objetivos durante la ejecución de la tarea. Una vez que todos los objetivos estén cumplidos, la tarea se dará por completada.

INSTRUCCIONES CRÍTICAS:
1. Si la tarea no lo especifica, inicia con proponerle al estudiante una forma dinámica de cumplir con la tarea para cumplir con sus objetivos (puede ser un juego de rol, una situación, etc.)
2. Si se trata de un juego de rol, ten en cuenta: El juego de rol debe ser natural y únicamente debes hablar en francés mientras estén en el juego de rol. No des explicaciones, solo fluye en la converación sin hacer correcciones durante el juego de rol. Si han iniciado un juego de rol, el usuario se encargará de decirte cuando detenerse. Si el usuario hace preguntas en medio del juego de rol, busca la manera de resolverla sin perder tu personaje. No coloques viñetas, ni parentesis ni indiques que es tu turno de hablar; ni indicaciones ni parentesis.
3. ENFÓCATE PRINCIPALMENTE EN LOS OBJETIVOS PENDIENTES. Si un objetivo ya fue cumplido, no necesitas volver a trabajarlo a menos que el estudiante lo mencione específicamente.

Recuerda: La tarea es el contexto/situación, pero los objetivos son lo que realmente debe cumplir el estudiante. La actividad se completa cuando todos los objetivos están cumplidos.`;

      // Actualizar el prompt completo con la información actualizada
      const updatedPrompt = fullPrompt + taskInstructions;
      configService.setFullPrompt(updatedPrompt);
      this.currentPrompt = updatedPrompt;

      logger.log('[WebSocketService] ✅ Prompt actualizado con progreso de objetivos:', {
        completed: completedObjectives.length,
        pending: pendingObjectives.length
      });
    } catch (error) {
      console.error('[WebSocketService] Error al actualizar prompt con objetivos:', error);
    }
  }

  /**
   * Procesar transcripción de audio recibida del WebSocket
   * El WebSocket procesa el audio con Gemini y devuelve el texto transcrito
   */
  processTranscription(transcribedText: string): void {
    // IMPORTANTE: Si había una respuesta interrumpida, este es el momento de limpiarla completamente
    // La transcripción indica que el usuario ya envió su nuevo mensaje, por lo que la respuesta
    // anterior ya debería haber terminado de llegar (o está a punto de terminar)
    if (this.isResponseInterrupted) {
      this.cleanupInterruptedResponse();
    }

    // Procesar el texto transcrito como mensaje del usuario
    // IMPORTANTE: Si había una respuesta interrumpida, este es el momento de limpiarla completamente
    // La transcripción indica que el usuario ya envió su nuevo mensaje, por lo que la respuesta
    // anterior ya debería haber terminado de llegar (o está a punto de terminar)
    if (this.isResponseInterrupted) {
      this.cleanupInterruptedResponse();
    }
    
    const trimmedText = transcribedText.trim();
    if (!trimmedText || trimmedText.length === 0) {
      // Transcripción vacía: activar flag pero no agregar al historial
      this.lastUserMessageWasEmpty = true;
      conversationSaverService.incrementNotValidCount(); // Contar mensaje no válido
      this.expectingTranscription = false;
      return;
    }
    
    // Cuando llega la transcripción de Gemini, agregarla al chathistory
    const message: ChatMessage = {
      role: 'user',
      content: '[audio] ' + stripChannelPrefix(trimmedText),
      timestamp: Date.now(),
    };

    this.chathistory.push(message);
    this.lastUserMessageWasEmpty = false; // Resetear flag ya que el mensaje no está vacío

    // Detener contador de tiempo muerto cuando el usuario habla (transcripción recibida)
    conversationSaverService.stopIdleTimer();

    // NO registrar interacción aquí - se cuenta por conversación, no por mensaje

    // Notificar al callback para actualizar la UI
    if (this.onMessageCallback) {
      this.onMessageCallback(message);
    }

    // Evaluar mensaje para objetivos académicos si está activo
    if (this.academicMode.isActive) {
      this.evaluateUserMessageForObjectives(trimmedText).catch((err) => { /* console.error(err); */ });
    }
    
    this.expectingTranscription = false;
  }

  /**
   * Procesar respuesta del WebSocket (chunk de texto)
   * Encola el chunk para procesamiento 1:1 con su audio correspondiente
   */
  processTextChunk(chunk: string): void {
    // Ignorar chunks mientras se están limpiando las colas
    if (this.isClearingQueues) {
      return;
    }

    // Si la respuesta fue interrumpida, descartar
    if (this.isResponseInterrupted) {
      this.interruptedResponseChunks.push(chunk);
      return;
    }

    if (!chunk || chunk.trim() === '') {
      return;
    }

    // Llego un chunk nuevo: cancelar el finish pendiente si lo habia
    this.cancelPendingFinish();

    // 1) PINTAR EL TEXTO DE INMEDIATO (desacoplado del audio).
    // Antes el texto sólo se mostraba dentro de tryProcessNextChunkPair, que
    // espera a que termine de sonar el audio del chunk anterior (gate
    // isPlayingAudio). Eso hacía que la respuesta se revelara frase a frase al
    // ritmo de la voz. Ahora el texto aparece apenas llega del WebSocket, así la
    // respuesta completa se va escribiendo de corrido mientras el audio sigue
    // reproduciéndose en orden por su cuenta.
    this.renderTextChunkImmediate(chunk);

    // 2) ENCOLAR el chunk para emparejarlo con su audio y reproducirlo en orden.
    // Capturamos receivedAt AQUÍ (no en tryProcessNextChunkPair) porque este es el momento real
    // en que el chunk llegó del WebSocket, antes de cualquier espera por reproducción de audio.
    // Es la métrica correcta para calcular TTFT y latencia del servidor.
    this.messageQueue.push({ chunk, receivedAt: Date.now() });

    // Intentar reproducir el siguiente par de audio si ambos están disponibles
    this.tryProcessNextChunkPair();
  }

  /**
   * Pinta el texto de un chunk en la burbuja del asistente de inmediato, sin
   * esperar al audio. Es la porción de "texto" que antes vivía dentro de
   * tryProcessNextChunkPair, extraída aquí para desacoplarla de la reproducción.
   * Una respuesta = una burbuja: crea la burbuja en el primer chunk del turno y
   * la extiende en los siguientes. NO toca el audio.
   */
  private renderTextChunkImmediate(textChunk: string): void {
    // Limpiar espacios en blanco antes y después
    const cleanedText = textChunk.trim();

    // Si el texto está vacío después del trim, no hay nada que pintar (el audio,
    // si llega, se gestiona luego en tryProcessNextChunkPair).
    if (!cleanedText || cleanedText.length === 0) {
      return;
    }

    // Chunk de sólo puntuación/símbolos (ej. "?" suelto): lo adjuntamos al final
    // de la última burbuja del asistente, sin espacio y sin crear burbuja nueva.
    if (/^[\p{P}\p{S}\s]+$/u.test(cleanedText)) {
      const lastIdx = this.chathistory.length - 1;
      const last = lastIdx >= 0 ? this.chathistory[lastIdx] : null;
      if (last && last.role === 'assistant') {
        last.content = `${last.content}${cleanedText}`;
        this.accumulatedChunks += cleanedText;
        // Notificar UI para que re-renderice la última burbuja con el símbolo adjunto
        if (this.onMessageCallback) {
          this.onMessageCallback(last);
        }
      }
      return;
    }

    // Una respuesta = una burbuja: si el último mensaje del historial ya es del
    // asistente, este chunk extiende esa burbuja (turno en curso). Si es del usuario
    // (o no existe), abrimos una burbuja nueva (es el primer chunk del turno).
    const lastIdx = this.chathistory.length - 1;
    const last = lastIdx >= 0 ? this.chathistory[lastIdx] : null;
    const shouldAppendToBubble = last !== null && last.role === 'assistant';

    if (shouldAppendToBubble && last) {
      // Extender la burbuja de asistente existente con el nuevo chunk
      last.content = last.content.length > 0 ? `${last.content} ${cleanedText}` : cleanedText;
      // Notificar UI para que re-renderice la burbuja con el contenido extendido
      if (this.onMessageCallback) {
        this.onMessageCallback(last);
      }
    } else {
      // Primer chunk del turno: crear nueva burbuja (aún sin audio; la URL se
      // adjunta en tryProcessNextChunkPair cuando llega el audio de este chunk).
      const wasEmptyMessage = this.lastUserMessageWasEmpty;
      if (wasEmptyMessage) {
        this.lastUserMessageWasEmpty = false;
      }

      const message: ChatMessage = {
        role: 'assistant',
        content: cleanedText,
        timestamp: Date.now(),
        receivedAt: Date.now(),
        audioUrls: [], // se irá llenando a medida que lleguen los audios del turno
        afterInvalidMessage: wasEmptyMessage,
      };

      this.chathistory.push(message);

      if (this.onMessageCallback) {
        this.onMessageCallback(message);
      }
    }

    // Acumular para consolidación final (con un espacio entre chunks)
    if (this.accumulatedChunks.length > 0) {
      this.accumulatedChunks += ' ';
    }
    this.accumulatedChunks += cleanedText;
  }

  /**
   * Procesa el siguiente par (texto + audio) SÓLO para reproducir el audio en
   * orden. El TEXTO ya fue pintado por renderTextChunkImmediate al recibirse el
   * chunk; aquí no se modifica el contenido de la burbuja, sólo se adjunta la URL
   * de audio (para la re-escucha y el indicador 🔊) y se reproduce manteniendo la
   * secuencia sonora (1 audio a la vez, gate isPlayingAudio).
   */
  private tryProcessNextChunkPair(): void {
    // Solo procesar si:
    // 1. NO está reproduciendo audio actualmente
    // 2. Hay texto Y audio disponibles en sus colas
    if (this.isPlayingAudio) {
      return;
    }

    if (this.messageQueue.length === 0 || this.audioQueue.length === 0) {
      return;
    }

    // Dequeue un par: texto + audio
    const textChunkItem = this.messageQueue.shift()!;
    const audioItem = this.audioQueue.shift()!;

    // Limpiar espacios en blanco antes y después
    const cleanedText = textChunkItem.chunk.trim();

    // Si el texto está vacío después del trim, no hay burbuja pero sí reproducimos audio
    if (!cleanedText || cleanedText.length === 0) {
      this.playAudioChunk(audioItem);
      return;
    }

    // Chunk de sólo puntuación/símbolos: el texto ya se adjuntó en
    // renderTextChunkImmediate; aquí sólo descartamos su audio (para que Galia no
    // "lea" el símbolo) y seguimos con el siguiente par.
    if (/^[\p{P}\p{S}\s]+$/u.test(cleanedText)) {
      // Liberar la URL del audio descartado para no fugar memoria
      try { URL.revokeObjectURL(audioItem.url); } catch { /* noop */ }
      // Continuar con el siguiente par si lo hay
      this.tryProcessNextChunkPair();
      return;
    }

    // Guardar el blob del chunk de audio para reproducir después (replay-on-tap)
    this.audioBlobStore.set(audioItem.url, audioItem.blob);

    // Adjuntar la URL de audio a la burbuja actual del asistente (re-escucha +
    // indicador 🔊). El texto NO se toca aquí (ya está pintado).
    const lastIdx = this.chathistory.length - 1;
    const last = lastIdx >= 0 ? this.chathistory[lastIdx] : null;
    if (last && last.role === 'assistant') {
      if (!last.audioUrls) {
        last.audioUrls = last.audioUrl ? [last.audioUrl] : [];
      }
      last.audioUrls.push(audioItem.url);
      if (!last.audioUrl) {
        last.audioUrl = audioItem.url; // primer audio del turno → activa 🔊
      }
      // Notificar UI para que muestre el indicador de audio
      if (this.onMessageCallback) {
        this.onMessageCallback(last);
      }
    }

    // Reproducir audio del chunk
    this.playAudioChunk(audioItem);
  }

  /**
   * Reproduce un chunk de audio
   * Cuando termina, intenta procesar el siguiente par automáticamente
   */
  private playAudioChunk(audioItem: { url: string; blob: Blob }): void {
    // Si hay audio manual reproduciéndose, saltar este chunk automático
    // El audio manual tiene prioridad
    if (this.isManualAudioPlaying) {
      // Saltar este chunk - no reproducir
      this.isPlayingAudio = false;
      // Si hay más pares en cola, intentar procesar el siguiente después
      // Pero solo si el audio manual termina
      return;
    }

    // SILENCIADO: el usuario no quiere escuchar a Galia (solo texto). El texto ya
    // se pintó (renderTextChunkImmediate) y la URL del audio ya quedó adjunta a la
    // burbuja para re-escucha manual si reactiva el sonido. Aquí NO reproducimos,
    // pero mantenemos viva la cadena de la cola para que la respuesta finalice
    // igual (avatar en silencio, sin animación de habla).
    if (!this.audioEnabled) {
      this.isPlayingAudio = false;
      if (this.messageQueue.length > 0 && this.audioQueue.length > 0) {
        this.tryProcessNextChunkPair();
        return;
      }
      if (this.responseEndMarkerReceived) {
        this.finishResponse();
      } else {
        this.scheduleFinishResponse();
      }
      return;
    }

    this.isPlayingAudio = true;
    
    // Activar avatar
    if (this.onAvatarStateCallback) {
      this.onAvatarStateCallback(true);
    }
    
    const audio = new Audio(audioItem.url);
    this.currentAudioElement = audio;
    
    audio.onended = () => {
      // NO revocar la URL aquí, mantenerla para reproducir después al hacer click
      // URL.revokeObjectURL(audioItem.url); // Comentado para permitir reproducción posterior
      this.isPlayingAudio = false;
      this.currentAudioElement = null;

      // Si hay mas pares listos, seguir inmediatamente
      if (this.messageQueue.length > 0 && this.audioQueue.length > 0) {
        this.tryProcessNextChunkPair();
        return;
      }

      // Colas vacias. Si ya recibimos el marker de fin del backend, sabemos
      // que NO viene mas — finalizamos sin grace. Si no, esperamos el grace
      // por si el siguiente chunk esta en camino (latencia variable o backend
      // viejo sin marker).
      if (this.responseEndMarkerReceived) {
        this.finishResponse();
      } else {
        this.scheduleFinishResponse();
      }
    };
    
    audio.onerror = (error) => {
      console.error(`[${this.sessionId}] [Audio] Error al reproducir chunk:`, error);
      // Limpiar del store si hay error
      this.audioBlobStore.delete(audioItem.url);
      this.isPlayingAudio = false;
      this.currentAudioElement = null;

      if (this.messageQueue.length > 0 && this.audioQueue.length > 0) {
        this.tryProcessNextChunkPair();
      } else {
        this.scheduleFinishResponse();
      }
    };

    audio.play().catch((error) => {
      console.warn(`[${this.sessionId}] [Audio] Error al iniciar chunk:`, error);
      this.isPlayingAudio = false;
      this.currentAudioElement = null;
      if (this.messageQueue.length > 0 && this.audioQueue.length > 0) {
        this.tryProcessNextChunkPair();
      } else {
        this.scheduleFinishResponse();
      }
    });
  }

  /**
   * Programa finishResponse con un grace period. Si llega un chunk nuevo durante
   * ese intervalo, el timer se cancela en processTextChunk / audio onmessage.
   */
  private scheduleFinishResponse(): void {
    if (this.pendingFinishTimer !== null) {
      clearTimeout(this.pendingFinishTimer);
    }
    this.pendingFinishTimer = setTimeout(() => {
      this.pendingFinishTimer = null;
      // Re-chequear por si llegaron chunks justo antes del fire
      if (this.messageQueue.length > 0 && this.audioQueue.length > 0) {
        this.tryProcessNextChunkPair();
        return;
      }
      this.finishResponse();
    }, WebSocketService.FINISH_GRACE_MS);
  }

  /** Cancela un finishResponse pendiente — llamarse cuando llega un chunk nuevo */
  private cancelPendingFinish(): void {
    if (this.pendingFinishTimer !== null) {
      clearTimeout(this.pendingFinishTimer);
      this.pendingFinishTimer = null;
    }
  }


  /**
   * Finalizar respuesta (cuando termina el audio)
   * NO consolida - mantiene los chunks como burbujas separadas
   * Esto permite que cada burbuja tenga su propio audio reproducible
   */
  finishResponse(): void {
    // Reset del marker — la proxima respuesta arrancara limpia.
    this.responseEndMarkerReceived = false;

    // NO consolidar - mantener los chunks individuales como burbujas separadas
    // Esto permite:
    // 1. Ver cada chunk como burbuja separada (mejor UX)
    // 2. Reproducir el audio de cada burbuja individualmente

    // Evaluar la calidad de la respuesta del asistente si hay modo académico activo
    if (this.academicMode.isActive && this.accumulatedChunks.trim().length > 0) {
      this.evaluateAssistantResponse(this.accumulatedChunks.trim()).catch(() => {});
    }
    
    // Limpiar acumulador
    this.accumulatedChunks = '';

    // NO registrar interacción aquí - se cuenta por conversación, no por mensaje

    // Iniciar contador de tiempo muerto cuando el asistente termina de responder
    conversationSaverService.startIdleTimer();

    // El estudiante estuvo escuchando/leyendo la respuesta: cuenta como
    // actividad para el tiempo de uso (aunque no haya tocado la pantalla).
    interactionTimeService.registerActivity();

    // SAVE POR TURNO: persistir la conversación a Supabase apenas Galia termina
    // de responder. Esto cierra la ventana de pérdida de datos del setInterval de
    // 30s en App.tsx — si el usuario cierra el navegador o pierde conexión justo
    // después de un turno, los mensajes quedan guardados.
    //
    // - Fire-and-forget (sin await): no bloquea UI ni el siguiente turno.
    // - Idempotente: guardarData verifica si hay mensajes nuevos antes de mandar
    //   (ver conversationSaverService.ts:280), así que llamarlo más seguido no
    //   produce duplicados ni saves innecesarios.
    // - El setInterval de 30s en App.tsx se mantiene como red de seguridad
    //   (belt + suspenders) en caso de que esta llamada falle silenciosamente.
    if (this.chathistory.length > 0) {
      const finalMessages = this.chathistory.map((m) => ({
        role: m.role,
        content: m.content,
        afterInvalidMessage: m.afterInvalidMessage,
      }));
      conversationSaverService.guardarData(finalMessages).catch(() => {
        // Error silencioso: el setInterval volverá a intentar en <30s.
      });
    }

    // Desactivar animación del avatar
    if (this.onAvatarStateCallback) {
      this.onAvatarStateCallback(false);
    }

    // Limpiar cola de audio
    this.audioQueue.forEach(item => URL.revokeObjectURL(item.url));
    this.audioQueue = [];
  }

  /**
   * Obtener el historial de chat
   */
  getChatHistory(): ChatMessage[] {
    return [...this.chathistory];
  }

  /**
   * Agregar mensaje del sistema al historial de chat
   * Útil para mensajes de bienvenida o información del sistema
   */
  addSystemMessage(content: string): void {
    const systemMessage: ChatMessage = {
      role: 'assistant',
      content: content,
      timestamp: Date.now(),
    };
    this.chathistory.push(systemMessage);
    
    // Notificar al callback si existe
    if (this.onMessageCallback) {
      this.onMessageCallback(systemMessage);
    }
  }

  /**
   * Obtener URL de audio para un mensaje (recrear si es necesario)
   * Útil para reproducir audio al hacer click en burbujas
   */
  getAudioUrlForMessage(audioUrl: string): string | null {
    // Si el blob está en el store, recrear la URL si es necesario
    const blob = this.audioBlobStore.get(audioUrl);
    if (blob) {
      // La URL original debería seguir funcionando si no se revocó
      // Si no funciona, recrear desde el blob
      return audioUrl;
    }
    return null;
  }

  /**
   * Recrear URL de audio desde blob almacenado
   */
  recreateAudioUrl(audioUrl: string): string | null {
    const blob = this.audioBlobStore.get(audioUrl);
    if (blob) {
      return URL.createObjectURL(blob);
    }
    return null;
  }

  /**
   * Cargar historial de chat
   */
  loadChatHistory(messages: ChatMessage[]): void {
    this.chathistory = [...messages];
    // Avisar a la UI (useWebSocket re-lee getChatHistory()): antes solo se
    // llamaba ANTES de conectar y nadie escuchaba; con las pestañas de
    // conversaciones recientes se rehidrata con el chat abierto.
    if (this.onChatHistoryClearedCallback) {
      this.onChatHistoryClearedCallback();
    }
  }

  /**
   * Limpiar historial de chat
   */
  clearChatHistory(): void {
    this.chathistory = [];
    // Notificar que el historial fue limpiado
    if (this.onChatHistoryClearedCallback) {
      this.onChatHistoryClearedCallback();
    }
  }

  /**
   * Registrar callback para cuando se limpia el historial
   */
  onChatHistoryCleared(callback: () => void): void {
    this.onChatHistoryClearedCallback = callback;
  }

  /**
   * Desregistrar callback de historial limpiado
   */
  offChatHistoryCleared(): void {
    this.onChatHistoryClearedCallback = undefined;
  }

  /**
   * Registrar callback de estado de transcripción (STT audio→texto).
   * Estados: 'transcribing' (audio enviado, esperando texto), 'done' (reconocido),
   * 'not_recognized' (STT no entendió: vacío/alucinación o parse fallido).
   */
  onTranscriptionState(callback: (state: 'transcribing' | 'done' | 'not_recognized') => void): void {
    this.onTranscriptionStateCallback = callback;
  }

  /** Desregistrar callback de estado de transcripción */
  offTranscriptionState(): void {
    this.onTranscriptionStateCallback = undefined;
  }

  /**
   * Enviar mensaje inicial con el system prompt
   * Se debe llamar después de que el WebSocket se conecte y el usuario presione "Aceptar"
   */
  sendInitialMessage(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    if (!this.currentPrompt || this.currentPrompt.trim() === '') {
      // console.warn('[WebSocket] No se puede enviar mensaje inicial - prompt no disponible');
      return;
    }
    
    // Enviar mensaje inicial con el system prompt
    const encodeBase64 = btoa('starting');
    const systemContext = [{ role: 'system', content: this.currentPrompt }];
    const payload = JSON.stringify({
      context: JSON.stringify(systemContext),
      entry_format: 'audio',
      audio: encodeBase64,
      model: this.mainModel,
      llm: this.mainLLM,
      llm_transcription_model: this.transcriptionModel,
    });
    
    try {
      this.ws.send(payload);
      logger.log('[WS-DIAG] sendInitialMessage enviado — bytes:', payload.length);
    } catch (error) {
      console.error('[WS-DIAG] Error al enviar mensaje inicial:', error);
    }
  }

  /**
   * Barge-in: el usuario tocó el mic mientras Galia hablaba.
   * Detiene la reproducción, vacía las colas y marca la respuesta como
   * interrumpida para descartar chunks que sigan llegando. El flag de
   * interrupción se resetea cuando el usuario realmente envíe su mensaje
   * (sendMessage línea 880). Soluciona pendientes #4 y #5 (Santiago 2026-05-26).
   */
  public interruptPlayback(): boolean {
    const wasActive = this.isPlayingAudio
      || this.messageQueue.length > 0
      || this.audioQueue.length > 0;
    if (!wasActive) return false;
    this.interruptCurrentResponse();
    return true;
  }

  /**
   * Limpiar todas las colas de audio y mensajes pendientes
   */
  clearAllQueues(): void {
    // Activar flag para ignorar mensajes entrantes durante la limpieza
    this.isClearingQueues = true;

    // Cancelar finishResponse pendiente (no debe dispararse tras limpiar)
    this.cancelPendingFinish();
    this.responseEndMarkerReceived = false;

    // Detener audio actual
    this.stopAutomaticAudio();
    
    // Limpiar colas de mensajes y audio
    this.messageQueue.forEach(() => {});
    this.messageQueue = [];
    
    this.audioQueue.forEach(item => URL.revokeObjectURL(item.url));
    this.audioQueue = [];
    
    // Limpiar acumuladores
    this.accumulatedChunks = '';
    this.interruptedResponseChunks = [];
    this.interruptedAudioChunks = [];
    
    // Desactivar flag después de un breve delay para permitir que cualquier mensaje en tránsito se descarte
    setTimeout(() => {
      this.isClearingQueues = false;
    }, 100);
  }

  /**
   * Callbacks
   */
  onMessage(callback: (message: ChatMessage) => void): void {
    this.onMessageCallback = callback;
  }

  offMessage(): void {
    this.onMessageCallback = undefined;
  }

  /**
   * Registrar callback para cuando la conexión esté completamente lista
   */
  onConnectionReady(callback: () => void): void {
    this.onConnectionReadyCallback = callback;
    // Si ya está conectado, llamar inmediatamente
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      callback();
    }
  }

  /**
   * Desregistrar callback de conexión lista
   */
  offConnectionReady(): void {
    this.onConnectionReadyCallback = undefined;
  }

  /**
   * Verificar si el WebSocket está completamente conectado
   */
  isWebSocketReady(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  onAudio(callback: (audioUrl: string) => void): void {
    this.onAudioCallback = callback;
  }

  offAudio(): void {
    this.onAudioCallback = undefined;
  }

  onAvatarState(callback: (isHablando: boolean) => void): void {
    this.onAvatarStateCallback = callback;
  }

  offAvatarState(): void {
    this.onAvatarStateCallback = undefined;
  }

  /**
   * Reconectar si se pierde la conexión
   */
  private reconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Máximo de intentos de reconexión alcanzado');
      return;
    }

    // Verificar que el prompt esté disponible antes de reconectar
    const config = configService.getConfig();
    if (!config.fullPrompt || config.fullPrompt.trim() === '') {
      // console.error('[WebSocket] No se puede reconectar: prompt no disponible');
      this.isConnecting = false;
      return;
    }

    this.reconnectAttempts++;
    setTimeout(() => {
      this.initialize();
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  /**
   * Detener reproducción automática de audio
   * Útil cuando el usuario quiere reproducir un audio manualmente
   */
  stopAutomaticAudio(): void {
    if (this.currentAudioElement) {
      this.currentAudioElement.pause();
      this.currentAudioElement = null;
    }
    this.isPlayingAudio = false;
    // Desactivar avatar si estaba hablando
    if (this.onAvatarStateCallback) {
      this.onAvatarStateCallback(false);
    }
  }

  /**
   * Silenciar / reactivar el audio (TTS) de Galia.
   * - enabled=false: deja de reproducir el audio automático (solo texto) y, si hay
   *   audio sonando, lo detiene; los chunks restantes se drenan sin sonar.
   * - persist=true (estudiante): guarda la preferencia en localStorage. En modo
   *   profesor pasar persist=false (el default profesor es silenciado por sesión).
   * El flag viaja además en cada mensaje (audio_enabled) para que el backend pueda
   * omitir la generación de TTS y ahorrar uso.
   */
  setAudioEnabled(enabled: boolean, persist: boolean = true): void {
    this.audioEnabled = enabled;
    if (persist) {
      try { localStorage.setItem('galia_audio_enabled', enabled ? 'true' : 'false'); } catch { /* noop */ }
    }
    if (!enabled) {
      // Detener el audio que suena ahora; el texto sigue, lo pendiente se drena.
      if (this.currentAudioElement) {
        this.currentAudioElement.pause();
        this.currentAudioElement = null;
      }
      this.isPlayingAudio = false;
      if (this.onAvatarStateCallback) {
        this.onAvatarStateCallback(false);
      }
      // Drenar la cola actual para no dejar la respuesta colgada.
      this.tryProcessNextChunkPair();
    }
  }

  getAudioEnabled(): boolean {
    return this.audioEnabled;
  }

  /**
   * Notificar al servicio si hay audio manual reproduciéndose
   */
  setManualAudioPlaying(isPlaying: boolean): void {
    this.isManualAudioPlaying = isPlaying;
    // Si el audio manual terminó y hay chunks en cola, intentar procesar el siguiente
    if (!isPlaying && this.messageQueue.length > 0 && this.audioQueue.length > 0) {
      // Intentar procesar el siguiente par después de un breve delay
      setTimeout(() => {
        if (!this.isManualAudioPlaying && !this.isPlayingAudio) {
          this.tryProcessNextChunkPair();
        }
      }, 100);
    }
  }

  /**
   * Verificar si audio manual está reproduciéndose
   */
  isManualAudioActive(): boolean {
    return this.isManualAudioPlaying;
  }

  /**
   * Cerrar conexión
   */
  disconnect(): void {
    
    // Remover listener de visibilidad
    if (this.visibilityChangeHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }
    
    // Limpiar audio en reproducción
    if (this.currentAudioElement) {
      this.currentAudioElement.pause();
      this.currentAudioElement = null;
    }
    this.isPlayingAudio = false;
    this.wasPlayingBeforeHidden = false;
    this.isManualAudioPlaying = false;
    
    // Limpiar colas de mensajes y audio
    this.messageQueue = [];
    this.audioQueue.forEach(item => URL.revokeObjectURL(item.url));
    this.audioQueue = [];
    this.accumulatedChunks = '';
    this.interruptedResponseChunks = [];
    this.interruptedAudioChunks = [];
    this.pendingSendQueue = [];
    
    // No limpiar chathistory aquí: al cerrar/abrir el panel el usuario debe seguir viendo su conversación.
    // El historial solo se limpia con clearChatHistory() o al cargar uno nuevo con loadChatHistory().

    // Resetear flags
    this.isResponseInterrupted = false;
    this.expectingTranscription = false;
    this.lastUserMessageWasEmpty = false;
    this.isClearingQueues = false;
    
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      this.ws = null;
    }
    this.isConnecting = false;
    this.isInitializing = false;
    this.reconnectAttempts = 0;
    
    // Limpiar callback de conexión lista
    this.onConnectionReadyCallback = undefined;
  }
  
  /**
   * Controlar si se pueden enviar mensajes
   */
  setCanSendMessage(canSend: boolean): void {
    this.canSendMessage = canSend;
  }
}

export const webSocketService = new WebSocketService();
export default webSocketService;


